import { flagConstants } from 'constants/index';
import { useCallback, useEffect, useState } from 'react';
import { IUserDetailsInfo } from 'teko-oauth2';
import { EvaluatedFlags, FlagsupClientBuilder } from 'flagsup-ts';
import configHelpers from 'helpers/config';
import trackingHelpers from 'helpers/tracking';

const { getCommonConfig } = configHelpers;
const { nonBlockTrackRespTimeWithStartAt } = trackingHelpers;
const { FLAG_KEYS, EXTENDED_FLAG_KEYS } = flagConstants;
const { apiFlagsupUrl, env } = getCommonConfig();

const FLAGSUP_CLIENT = new FlagsupClientBuilder()
  .setTargetHostname(apiFlagsupUrl)
  .setMaxRetries(3)
  .setAllowUseStaleTime(30 * 60) // 30 min in seconds
  .setEnableLogging(env === 'DEV');

const useFeatureFlags = (
  currentUser?: IUserDetailsInfo,
  currentSellerId?: number
) => {
  const [featureFlags, setFeatureFlags] = useState<EvaluatedFlags>({});
  const [isInitiating, setIsInitiating] = useState(true);

  const getFlags = useCallback(
    async (flagClient: FlagsupClientBuilder, userDetail: IUserDetailsInfo) => {
      setIsInitiating(true);
      const startAt = performance.now();
      const { error, flags } = await flagClient.getFlags(
        {
          ...FLAG_KEYS,
          ...EXTENDED_FLAG_KEYS,
        },
        userDetail
      );

      if (error) {
        nonBlockTrackRespTimeWithStartAt('batch-eval-flags-error', startAt, {
          error,
        });
        setIsInitiating(false);
        return;
      }

      if (flags) {
        nonBlockTrackRespTimeWithStartAt('batch-eval-flags', startAt, {
          flagData: flags,
        });
        setFeatureFlags(flags);
      }
      setIsInitiating(false);
    },
    []
  );

  // Set the default entity (user) and subscribe to flag changes
  useEffect(() => {
    if (!currentUser?.sub) {
      return;
    }

    const unsubscribe = FLAGSUP_CLIENT.setDefaultEntityId(
      currentUser.sub
    ).subscribe(updatedFlags => {
      setFeatureFlags(updatedFlags);
    });

    return () => {
      unsubscribe();
    };
  }, [currentUser?.sub]);

  // Evaluate flags only once the single active seller is resolved.
  // currentUser.meta_data.seller_id may hold multiple comma-separated ids (multi-seller
  // user), which cannot be matched by seller-targeted flags. We override it with the single
  // current seller id so every getEntityId callback resolves to one seller.
  useEffect(() => {
    if (!currentUser?.sub || !currentSellerId) {
      return;
    }

    const userDetailForFlags: IUserDetailsInfo = {
      ...currentUser,
      meta_data: {
        ...currentUser.meta_data,
        seller_id: String(currentSellerId),
      },
    };

    getFlags(FLAGSUP_CLIENT, userDetailForFlags);
  }, [currentUser?.sub, currentSellerId, getFlags]);

  return { featureFlags, isInitiating };
};

export default {
  useFeatureFlags,
};
